import { isEqual, omit } from "lodash";
import React, { Context, createContext, Dispatch, useContext, useEffect, useReducer } from "react";
import {
  AitTableAction,
  AitTableData,
  AitTableState,
  AitTableStateBase,
  LOAD_STATE,
  TABLE_CHANGE_PUSHED,
} from "./functions/interface";
import { tableReducer } from "./functions/tableReducer";
import { trProcessTable } from "./functions/trProcessTable";

interface TableContextProps<T> {
  state: AitTableState<T>;
  dispatch: Dispatch<AitTableAction<T>>;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const TableContext = createContext<TableContextProps<any> | null>(null);

interface TableContextProviderProps<T> {
  children: React.ReactNode;
  initialState: AitTableStateBase<T>;
  onChange: Dispatch<AitTableData<T>>;
}

/**
 * Provider that supplies table state and dispatch to child components.
 *
 * @template T - Table payload type
 * @param props - Component props
 * @param props.initialState - Initial state used to seed the table reducer
 * @param props.children - Child nodes wrapped by the provider
 * @param props.onChange - Callback invoked when table data is pushed
 */
const TableContextProviderInner = <T,>({
  initialState,
  children,
  onChange,
}: TableContextProviderProps<T>): React.ReactElement => {
  const [state, dispatch] = useReducer(tableReducer<T>, {
    ...initialState,
    changed: false,
    showOptionsWindow: [],
    processedTableData: trProcessTable(initialState),
  });

  // Update state from top down
  useEffect(() => {
    // Compare properties that are passed down
    const initialStateForComparison = {
      ...omit(initialState, ["changed", "showOptionsWindow", "processedTableData"]),
    };
    const currentStateForComparison = {
      ...omit(state, ["changed", "showOptionsWindow", "processedTableData"]),
    };

    const hasChanges = !isEqual(initialStateForComparison, currentStateForComparison);

    if (hasChanges) {
      if (state.changed) {
        onChange(state.originalTableData);
        dispatch({ operation: TABLE_CHANGE_PUSHED });
      } else {
        dispatch({ operation: LOAD_STATE, newState: initialState });
      }
    }
  }, [initialState, onChange, state]);

  return <TableContext.Provider value={{ state, dispatch }}>{children}</TableContext.Provider>;
};

// Memoized context provider to prevent unnecessary re-renders
export const TableContextProvider = React.memo(TableContextProviderInner) as <T>(
  props: TableContextProviderProps<T>,
) => React.ReactElement;

/**
 * Custom hook to access the table context.
 *
 * Returns a context object with { state, dispatch } or a safe stub when the
 * context is not available (useful for tests).
 *
 * @template T - Table payload type
 * @returns The table context object `{ state, dispatch }` or a safe stub when the context is not available (useful for tests).
 */
export function useTableContext<T>() {
  const context = useContext(TableContext as Context<TableContextProps<T> | null>);
  return !context
    ? {
        state: null,
        dispatch: () => console.error("WARNING: Dispatch not initiated"),
      }
    : context;
}

TableContextProviderInner.displayName = "TableContextProvider";
